import { ProCard } from '@ant-design/pro-components'
import { localQueryEntityOptions } from '@app/icare/client/db/client/localQueryOneOptions'
import { EntityDeleteButton } from '@app/icare/components/entity/EntityDeleteButton'
import { EntityDeletedBanner } from '@app/icare/components/entity/EntityDeletedBanner'
import { AcademicTitleEnum, type AcademicTitleType } from '@app/icare/db/schema/common'
import { DoctorId } from '@app/icare/entities/doctor/schema/Doctor'
import { useEntityDeletion } from '@app/icare/hooks/useEntityDeletion'
import { t } from '@lingui/core/macro'
import { zodParams } from '@pkg/db/utils'
import { formatAddress } from '@pkg/shared/utils'
import { useRouterTabs } from '@pkg/ui/hooks'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainer'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'
import { Tabs } from 'antd'
import { z } from 'zod'

export const Route = createFileRoute('/_app/praxen/$id')({
  component: RouteComponent,
  params: zodParams(z.object({ id: DoctorId.ref })),
  validateSearch: zodValidator(
    z.object({
      tab: z.enum(['general', 'contact_persons']).optional(),
    }),
  ),
  loader: () => ({
    breadcrumb: {
      label: t`Praxen`,
      to: '/praxen',
    },
  }),
})

function RouteComponent() {
  const params = Route.useParams()
  const { data } = useQuery(localQueryEntityOptions(params.id))
  const deletion = useEntityDeletion({ data, entityType: 'doctors' })
  const title =
    formatAddress([AcademicTitleEnum.$.getLabel(data?.title as AcademicTitleType), data?.name, data?.nameComplement]) ||
    'Neuer Eintrag'
  const tabs = useRouterTabs()

  usePageContainerOverride(
    {
      header: {
        title,
        extra: <EntityDeleteButton {...deletion} />,
        footer: (
          <Tabs
            {...tabs}
            items={[
              {
                key: `/praxen/${params.id}`,
                label: 'Allgemein',
              },
              {
                key: `/praxen/${params.id}/contact-persons`,
                label: 'Ansprechpartner',
              },
            ]}
          />
        ),
      },
    },
    [title, tabs.activeKey, deletion.isDeleted, deletion.isPending],
  )

  if (!data) {
    return <ProCard loading />
  }

  return (
    <>
      <EntityDeletedBanner {...deletion} />
      <Outlet />
    </>
  )
}
